// errors — a map from an API ERROR CODE to a sentence for the user.
// The keys are the literal codes the backend returns (`{"error": "<key>"}`), not
// names invented on the frontend. That way a new server-side code shows up as a
// missing translation instead of leaking a raw code to the customer.
// Keys mirror ../pl/errors.ts exactly.
//
// `consent_required` deliberately has NO sentence here — that message lives next
// to the field it belongs to (`checkout.consent.required`). Two copies of the
// same sentence in two places drift apart at the first edit.
export const errors = {
  // Payments / plan (P01).
  country_not_supported: 'We do not support payments in that country yet.',
  capacity_cap: 'This bundle would cover more than six months of publishing for the number of accounts you have. Add a slot first.',
  // These codes arrive DIFFERENTLY from every other one in this file: not through
  // `api()` but through a REDIRECT. `/billing/checkout`, `/billing/topup` and
  // `/billing/portal` are POSTed by a real <form> (the success path 303s to Stripe on
  // another origin, which `fetch` cannot follow), so until 2026-08-31 an error reached
  // no screen at all: `jsonify(...)` was RENDERED AS THE PAGE and replaced the app with
  // raw JSON. The server now 303s back with `?err=<code>` and the screen reads it through
  // the same `describeApiError` as every other denial — hence these sentences.
  //
  // None of them promises a retry, because retrying changes none of these states. The one
  // exception is `csrf_invalid`, where it does, and that sentence says so.
  plan_unknown: 'We do not know that plan - the link may be out of date. Pick a plan from the pricing page.',
  plan_not_purchasable: 'This plan cannot be bought right now, and that is on us rather than on you. Nothing was charged - write to us and we will tell you when it is back.',
  billing_unavailable: 'Payments are switched off on our side for the moment. Nothing was charged; try later or write to us.',
  invoice_document_unavailable: 'We could not open the PDF right now. Try again in a moment.',
  invoice_document_not_found: 'This payment has no PDF document available.',
  bundle_invalid: 'We do not recognise that token pack. Pick one from the list.',
  gem_pack_unknown: 'No such token pack exists. Pick one from the list.',
  gem_pack_not_for_plan: 'That pack comes with a higher plan. Change plan, or pick a smaller pack.',
  no_subscription: 'There is no active subscription here to manage. If a payment just went through, refresh in a moment.',
  csrf_invalid: 'Your session expired before we took the payment. Nothing was charged - sign in and try again.',
  // Publishing (P02, Accounts screen). Enumerated from `app/publishing/routes.py`; see
  // ../pl/errors.ts for why these five and why not the /publish codes.
  publishing_unavailable: 'Publishing is unavailable for a moment - that is on our side, not yours. Try again in a few minutes; nothing was lost.',
  invalid_slot_index: 'That slot does not exist. Refresh the page and try again.',
  not_found: 'We could not find that account - it may have been disconnected in another tab. Refresh the page.',
  creator_info_unavailable: 'TikTok did not answer when we asked about the limits on this account. Try again shortly - the connection itself is fine.',
  creator_info_unsupported: 'This account does not share its posting limits. Everything else works normally.',
  slot_limit_reached: 'You have used every slot - add a slot or disconnect an account.',
  insufficient_gems: 'Not enough tokens. Buy a bundle or wait for your plan to renew.',
  // Render (P00/P03): an upload longer than the edit limit is not rejected — we
  // trim it. This error only appears when a longer edit is explicitly requested.
  duration_too_long: 'An edit can be 30 seconds at most. Trim the track and try again.',
  // Last resort: a code the frontend does not know yet. We never show a raw code
  // or "Error 500".
  unknown: 'Something went wrong on our side. Try again in a moment - if it keeps happening, write to us.',
  // 403 denials with a code. Without a sentence here `describeApiError` falls back to
  // `unknown`, which says "our fault, try again" about things that are neither.
  // 409 conflicts. See the Polish file for why these are denials and not failures.
  // Has a twin in `appCampaigns.apiError.already_published` — the longer version for the
  // two campaign buttons (Try again / Remove from plan), where there is room for context
  // and here there is not. Correct this sentence, correct that one too.
  already_published: 'This has already been published.',
  campaign_cancelled: 'This campaign is cancelled.',
  campaign_not_editable: 'This campaign can no longer be edited - it is running or finished.',
  campaign_not_pausable: 'This campaign cannot be paused right now.',
  campaign_not_paused: 'This campaign is not paused.',
  gems_on_hold: 'The tokens for this campaign are held until it finishes or is cancelled.',
  no_audio: 'This track has no audio.',
  nothing_delivered: 'There is nothing to download - nothing has rendered yet.',
  pack_unavailable: 'This clip pack is unavailable.',
  render_in_flight: 'This render is already running. Wait for it to finish.',
  analysis_in_progress: 'That analysis is already running. Wait for it to finish.',
  not_retryable: 'There is nothing to re-analyse: this analysis is already counted.',
  track_in_use: 'This track is used by a series that is still running. Delete it once that series finishes.',
  email_unverified: 'Confirm your email address before signing in - we sent a link when you created the account. Check your spam folder too.',
  account_suspended: 'Your account is suspended. Write to us and we will look into it.',
  account_pending: 'Your account is waiting for approval. We will let you know when it is ready.',
  autopilot_disabled: 'Autopilot is switched off for this account.',
  // A failed render. NOT a denial (`describeApiError` never reads this) — it is the
  // sentence for the edit screen, which until now printed the server's raw technical
  // string. The second half is a statement about invariant #5, not about this row:
  // the refund is `refund:render:<id>` in the wallet and the status payload carries
  // no refund flag (see the comment at `refunded_*`).
  render_failed: 'This render did not finish. We do not keep tokens for it.',

  cta: {
    account_suspended: 'Write to us',
  },
}
